import type { EventFrame, Message, Part } from "@agentsaas/protocol";
import { isSdkMessage, toolResultText, type ContentBlock, type SdkMessage } from "../agent/sdk";

/**
 * 对话面板的数据模型：把 agent:<aid>:session 的事件（含 sdk.message 流）和 DM 里的用户消息
 * 归并成一条时间线。纯函数，便于测试；历史回放与实时流用同一个 reducer，按 seq 去重。
 */

export type ChatItem =
  | { kind: "user"; id: string; parts: Part[]; at: string }
  | { kind: "text"; id: string; text: string; at: string; streaming?: boolean }
  | { kind: "thinking"; id: string; text: string; at: string; streaming?: boolean }
  | { kind: "tool"; id: string; name: string; input: Record<string, unknown>; at: string; result?: { text: string; isError: boolean } }
  | { kind: "run"; id: string; status: "started" | "finished" | "interrupted" | "error"; at: string; costMicroUsd?: number; code?: string };

interface Partial {
  msgId: string;
  at: string;
  blocks: { type: "text" | "thinking" | "other"; text: string }[];
}

export interface ChatState {
  items: ChatItem[];
  partial: Partial | null;
  running: boolean;
  lastSeq: number;
  seenMessages: Set<string>;
}

export const initialChat = (): ChatState => ({ items: [], partial: null, running: false, lastSeq: 0, seenMessages: new Set() });

type SessionEvent = Pick<EventFrame, "seq" | "type" | "data" | "at">;

/** 应用一条会话事件（seq 不大于 lastSeq 的忽略） */
export function applyEvent(state: ChatState, e: SessionEvent): ChatState {
  if (e.seq <= state.lastSeq) return state;
  const s: ChatState = { ...state, lastSeq: e.seq };
  const d = e.data as Record<string, unknown>;
  switch (e.type) {
    case "run.started":
      return { ...s, running: true, items: [...s.items, { kind: "run", id: `run:${String(d.wakeId)}:start`, status: "started", at: e.at }] };
    case "run.finished": {
      const usage = d.usage as { costMicroUsd?: number } | undefined;
      return { ...s, running: false, partial: null, items: [...s.items, { kind: "run", id: `run:${String(d.wakeId)}:end`, status: "finished", at: e.at, costMicroUsd: usage?.costMicroUsd }] };
    }
    case "run.interrupted":
      return { ...s, running: false, partial: null, items: [...s.items, { kind: "run", id: `run:${String(d.wakeId)}:end`, status: "interrupted", at: e.at }] };
    case "run.error":
      return { ...s, running: false, partial: null, items: [...s.items, { kind: "run", id: `run:${String(d.wakeId)}:err:${e.seq}`, status: "error", at: e.at, code: String(d.code) }] };
    case "sdk.message":
      return isSdkMessage(d.payload) ? applySdk(s, d.payload, e.at) : s;
    default:
      return s;
  }
}

function applySdk(s: ChatState, m: SdkMessage, at: string): ChatState {
  switch (m.type) {
    case "stream_event": {
      const ev = m.event;
      if (ev.type === "message_start") return { ...s, partial: { msgId: ev.message.id, at, blocks: [] } };
      if (!s.partial) return s;
      if (ev.type === "content_block_start") {
        const blocks = [...s.partial.blocks];
        const b = ev.content_block;
        blocks[ev.index] = { type: b.type === "text" ? "text" : b.type === "thinking" ? "thinking" : "other", text: b.type === "text" ? b.text : b.type === "thinking" ? b.thinking : "" };
        return { ...s, partial: { ...s.partial, blocks } };
      }
      if (ev.type === "content_block_delta") {
        const blocks = [...s.partial.blocks];
        const cur = blocks[ev.index] ?? { type: ev.delta.type === "thinking_delta" ? ("thinking" as const) : ("text" as const), text: "" };
        const add = ev.delta.type === "text_delta" ? ev.delta.text : ev.delta.type === "thinking_delta" ? ev.delta.thinking : "";
        blocks[ev.index] = { ...cur, text: cur.text + add };
        return { ...s, partial: { ...s.partial, blocks } };
      }
      return s;
    }
    case "assistant": {
      const partial = s.partial && s.partial.msgId === m.message.id ? null : s.partial;
      const items = [...s.items];
      m.message.content.forEach((b: ContentBlock, i) => {
        const id = `${m.message.id}:${i}`;
        if (b.type === "text") items.push({ kind: "text", id, text: b.text, at });
        else if (b.type === "thinking") items.push({ kind: "thinking", id, text: b.thinking, at });
        else if (b.type === "tool_use") items.push({ kind: "tool", id: b.id, name: b.name, input: b.input ?? {}, at });
      });
      return { ...s, partial, items };
    }
    case "user": {
      if (typeof m.message.content === "string") return s;
      let items = s.items;
      for (const b of m.message.content) {
        if (b.type !== "tool_result") continue;
        const result = { text: toolResultText(b.content), isError: !!b.is_error };
        items = items.map((it) => (it.kind === "tool" && it.id === b.tool_use_id ? { ...it, result } : it));
      }
      return { ...s, items };
    }
    default:
      return s;
  }
}

/** DM 中的用户消息（按 id 去重，按时间插入） */
export function applyUserMessage(state: ChatState, m: Message): ChatState {
  if (m.from.type !== "user" || state.seenMessages.has(m.id)) return state;
  const seen = new Set(state.seenMessages).add(m.id);
  const item: ChatItem = { kind: "user", id: m.id, parts: m.parts, at: m.createdAt };
  const items = [...state.items];
  let i = items.length;
  while (i > 0 && items[i - 1]!.at > item.at) i--;
  items.splice(i, 0, item);
  return { ...state, items, seenMessages: seen };
}

/** 渲染用：把正在流式输出的部分附加到末尾 */
export function visibleItems(s: ChatState): ChatItem[] {
  if (!s.partial) return s.items;
  const tail: ChatItem[] = s.partial.blocks.flatMap((b, i): ChatItem[] =>
    b.type === "text"
      ? [{ kind: "text", id: `${s.partial!.msgId}:${i}:partial`, text: b.text, at: s.partial!.at, streaming: true }]
      : b.type === "thinking"
        ? [{ kind: "thinking", id: `${s.partial!.msgId}:${i}:partial`, text: b.text, at: s.partial!.at, streaming: true }]
        : [],
  );
  return [...s.items, ...tail];
}
